<script setup lang="ts">
import { toHead } from 'vue-datocms'
import { queryJob } from '~/graphql/job'
import { useSchemaGraph, buildSchemaWebPage } from '~/composables/schema'
import { schemaIds } from '~/types/schema'

const route = useRoute()

const { throwError } = useNuxtError()

const { data, error } = await useDatoQuery(queryJob, {
  variables: {
    slug: route.params.slug as string
  }
})

if (!data.value?.job || error.value) {
  console.error(error.value)
  throwError(404)
}

const isFormSubmitted = ref(false)

if (import.meta.env.SSR) {
  useTheme(AppTheme.DARK)
}

onMounted(() => {
  if (import.meta.client) {
    useTheme(AppTheme.DARK)
  }
})

useHead(() => {
  return toHead(data.value?.job?._seoMetaTags || [])
})

const config = useRuntimeConfig()
const { usePageSchema } = useSchemaGraph()
const ids = schemaIds(config.public.appUrl)
const base = config.public.appUrl.replace(/\/$/, '')

usePageSchema([
  buildSchemaWebPage({
    id: `${base}${route.path}#webpage`,
    url: `${base}${route.path}`,
    name: data.value?.job?.title ?? undefined,
    description: data.value?.job?.excerpt ?? undefined,
    inLanguage: 'en',
    isPartOf: { '@id': ids.WEBSITE }
  })
])
</script>

<template>
  <div v-if="data?.job">
    <article class="pt-header-h lg:pt-header-h-lg">
      <header class="mx-auto mt-8 max-w-editor px-4 lg:mt-15 lg:px-10">
        <div class="mb-8"><NuxtLink to="/careers">Careers</NuxtLink> / {{ data.job.title }}</div>
        <Heading v-if="data.job.title" :size="4">{{ data.job.title }}</Heading>
        <div class="mx-auto mb-8 mt-4 flex flex-row text-sm lg:mb-12 lg:mt-2.5">
          <div v-if="data.job.time">
            {{ data.job.time }}
          </div>
          <div class="mx-2">/</div>
          <div v-if="data.job.location">
            {{ data.job.location }}
          </div>
        </div>
        <div v-if="data.job.leadText" class="text-xl lg:text-2xl">
          {{ data.job.leadText }}
        </div>
      </header>
      <div class="mx-auto max-w-editor px-4 lg:px-10">
        <hr class="my-8 lg:my-12" />
      </div>
      <DatoStructuredText v-if="data.job.body" :data="data.job.body" class="editor-full" />
      <div class="mx-auto my-10 max-w-editor px-4 lg:my-20 lg:px-10">
        <Heading
          :size="undefined"
          class="mb-4 text-2xl-bl uppercase lg:mb-8 lg:text-3xl-bl"
          :is-semantic="false"
          >Apply for this Role</Heading
        >
        <div>
          <JobApplication v-if="!isFormSubmitted" @submit="isFormSubmitted = true" />
          <ThankYouMessage v-else message="Thank you!" class="min-h-[316px]" />
        </div>
      </div>
    </article>
  </div>
</template>
